/**
 * Space Manager
 * 
 * Provides focused space management with consistent patterns and enhanced performance
 */

import type { Space } from '@/lib/types';
import { generateNumericId } from '@/lib/utils';
import { format } from 'date-fns';
import { isSampleDataMode } from '@/lib/config/env';

type CreateSpaceData = Omit<Space, 'id' | 'dateCreated' | 'dateUpdated'>;
type UpdateSpaceData = Partial<Omit<Space, 'id' | 'dateCreated' | 'dateUpdated'>>;

export interface SpaceFilters {
  clubId?: string;
  organizationId?: string;
  facilityType?: string;
  courtType?: string;
  status?: string;
  hasLights?: boolean;
  search?: string;
  isActive?: boolean;
  sportType?: string;
}

// Private data and cache
let spaces: Space[] = [];
let cache: Space[] | null = null;
let lastCacheTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Initialize data
const initializeData = async (): Promise<void> => {
  if (spaces.length === 0) {
    try {
      // Only import sample data if we're in sample data mode
      if (isSampleDataMode()) {
        const sampleDataModule = await import('@/lib/sample-data');
        spaces = sampleDataModule.sampleSpacesData ? JSON.parse(JSON.stringify(sampleDataModule.sampleSpacesData)) : [];
      }
    } catch (importError) {
      console.warn('Sample spaces data not available:', importError);
      spaces = [];
    }
  }
};

// Initialize on module load
initializeData().catch(error => {
  console.error('Failed to initialize spaces data:', error);
  spaces = [];
});

const isCacheValid = (): boolean => {
  return cache !== null && (Date.now() - lastCacheTime) < CACHE_DURATION;
};

const getCachedData = (): Space[] => {
  initializeData();
  
  if (!isCacheValid()) {
    cache = JSON.parse(JSON.stringify(spaces));
    lastCacheTime = Date.now();
  }
  
  return cache!;
};

/**
 * Space management service for handling space-related operations
 */
class SpaceManager {
  /**
   * Get all spaces with optional filtering
   */
  async getAllSpaces(filters?: SpaceFilters): Promise<Space[]> {
    let spacesData = getCachedData();

    if (filters) {
      if (filters.clubId) {
        spacesData = spacesData.filter(space => space.clubId === filters.clubId);
      }
      
      if (filters.organizationId) {
        spacesData = spacesData.filter(space => space.organizationId === filters.organizationId);
      }
      
      if (filters.facilityType) {
        spacesData = spacesData.filter(space => space.facilityType === filters.facilityType);
      }
      
      if (filters.courtType) {
        spacesData = spacesData.filter(space => space.courtType === filters.courtType);
      }
      
      if (filters.status) {
        spacesData = spacesData.filter(space => space.status === filters.status);
      }
      
      if (typeof filters.hasLights === 'boolean') {
        spacesData = spacesData.filter(space => space.hasLights === filters.hasLights);
      }
      
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        spacesData = spacesData.filter(space => 
          space.displayName?.toLowerCase().includes(searchLower) ||
          space.description?.toLowerCase().includes(searchLower) ||
          space.facilityType.toLowerCase().includes(searchLower) ||
          space.courtType?.toLowerCase().includes(searchLower)
        );
      }
      
      if (typeof filters.isActive === 'boolean') {
        spacesData = spacesData.filter(space => 
          filters.isActive ? space.status === 'active' : space.status !== 'active'
        );
      }
      

    }

    return spacesData;
  }

  /**
   * Get spaces by club
   */
  async getSpacesByClub(clubId: string): Promise<Space[]> {
    return this.getAllSpaces({ clubId });
  }

  /**
   * Get spaces by organization
   */
  async getSpacesByOrganization(organizationId: string): Promise<Space[]> {
    return this.getAllSpaces({ organizationId });
  }

  /**
   * Get space by ID
   */
  async getSpaceById(id: string): Promise<Space | null> {
    const spacesData = getCachedData();
    return spacesData.find(space => space.id === id) || null;
  }

  /**
   * Create new space
   */
  async createSpace(data: CreateSpaceData): Promise<Space> {
    const now = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
    
    const newSpace: Space = {
      ...data,
      id: generateNumericId(10),
      dateCreated: now,
      dateUpdated: now,
    };

    spaces.push(newSpace);
    cache = null; // Invalidate cache
    
    return JSON.parse(JSON.stringify(newSpace));
  }

  /**
   * Update existing space
   */
  async updateSpace(id: string, updates: UpdateSpaceData): Promise<Space | null> {
    const spaceIndex = spaces.findIndex(space => space.id === id);
    
    if (spaceIndex === -1) {
      return null;
    }

    spaces[spaceIndex] = {
      ...spaces[spaceIndex],
      ...updates,
      dateUpdated: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
    };

    cache = null; // Invalidate cache
    
    return JSON.parse(JSON.stringify(spaces[spaceIndex]));
  }

  /**
   * Delete space
   */
  async deleteSpace(id: string): Promise<boolean> {
    const initialLength = spaces.length;
    spaces = spaces.filter(space => space.id !== id);
    
    if (spaces.length < initialLength) {
      cache = null; // Invalidate cache
      return true;
    }
    
    return false;
  }

  /**
   * Get space count by club
   */
  async getSpaceCountByClub(clubId: string): Promise<number> {
    const spacesData = await this.getSpacesByClub(clubId);
    return spacesData.length;
  }

  /**
   * Get space count by organization
   */
  async getSpaceCountByOrganization(organizationId: string): Promise<number> {
    const spacesData = await this.getSpacesByOrganization(organizationId);
    return spacesData.length;
  }

  /**
   * Get active spaces for organization
   */
  async getActiveSpacesForOrganization(organizationId: string): Promise<Space[]> {
    return this.getAllSpaces({ 
      organizationId,
      isActive: true 
    });
  }

  /**
   * Search spaces across multiple fields
   */
  async searchSpaces(query: string, organizationId?: string): Promise<Space[]> {
    return this.getAllSpaces({
      organizationId,
      search: query
    });
  }

  /**
   * Get spaces by facility type
   */
  async getSpacesByType(facilityType: string): Promise<Space[]> {
    return this.getAllSpaces({ facilityType });
  }

  /**
   * Validate space data
   */
  async validateSpaceData(data: CreateSpaceData | UpdateSpaceData): Promise<{
    isValid: boolean;
    errors: string[];
  }> {
    const errors: string[] = [];

    // Required field validation for new spaces
    if ('displayName' in data && !data.displayName?.trim()) {
      errors.push('Space display name is required');
    }

    if ('clubId' in data && !data.clubId?.trim()) {
      errors.push('Club ID is required');
    }

    if ('facilityType' in data && !data.facilityType?.trim()) {
      errors.push('Facility type is required');
    }

    // Capacity validation
    if (data.minCapacity !== undefined && data.maxCapacity !== undefined) {
      if (data.minCapacity > data.maxCapacity) {
        errors.push('Minimum capacity cannot be greater than maximum capacity');
      }
    }

    if (data.minCapacity !== undefined && data.minCapacity < 0) {
      errors.push('Minimum capacity cannot be negative');
    }

    if (data.maxCapacity !== undefined && data.maxCapacity < 1) {
      errors.push('Maximum capacity must be at least 1');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get space statistics
   */
  async getSpaceStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
    withLights: number;
    byFacilityType: Record<string, number>;
    byClub: Record<string, number>;
    byOrganization: Record<string, number>;
    bySportType: Record<string, number>;
  }> {
    const spacesData = getCachedData();
    
    const stats = {
      total: spacesData.length,
      active: spacesData.filter(s => s.status === 'active').length,
      inactive: spacesData.filter(s => s.status !== 'active').length,
      withLights: spacesData.filter(s => s.hasLights).length,
      byFacilityType: {} as Record<string, number>,
      byClub: {} as Record<string, number>,
      byOrganization: {} as Record<string, number>,
      bySportType: {} as Record<string, number>
    };

    // Group by facility type
    spacesData.forEach(space => {
      stats.byFacilityType[space.facilityType] = (stats.byFacilityType[space.facilityType] || 0) + 1;
    });

    // Group by club
    spacesData.forEach(space => {
      stats.byClub[space.clubId] = (stats.byClub[space.clubId] || 0) + 1;
    });

    // Group by organization
    spacesData.forEach(space => {
      stats.byOrganization[space.organizationId] = (stats.byOrganization[space.organizationId] || 0) + 1;
    });

    // Group by facility type instead of sport type
    spacesData.forEach(space => {
      if (space.facilityType) {
        stats.bySportType[space.facilityType] = (stats.bySportType[space.facilityType] || 0) + 1;
      }
    });

    return stats;
  }

  /**
   * Get spaces by multiple clubs
   */
  async getSpacesByClubs(clubIds: string[]): Promise<Space[]> {
    const spacesData = getCachedData();
    return spacesData.filter(space => clubIds.includes(space.clubId));
  }

  /**
   * Check if space exists
   */
  async spaceExists(id: string): Promise<boolean> {
    const space = await this.getSpaceById(id);
    return !!space;
  }

  /**
   * Get available spaces (active status)
   */
  async getAvailableSpaces(clubId?: string): Promise<Space[]> {
    return this.getAllSpaces({
      clubId,
      isActive: true
    });
  }
}

export const spaceManager = new SpaceManager();
export default spaceManager; 
